import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { t } from '../../common/i18n';
import Yup, { YupEmail, YupPassword, YupPhone } from '../../common/utils/FormValidation/index';

import {
  Col,
  Row,
  Text,
  TextField,
  Button,
  PasswordField,
  PhoneField,
  FormMessage,
  TipMessage,
} from '../../common/components';

import { IncompleteData } from './IncompleteData';

import { Link, useNavigate } from 'react-router-dom';
import { IAdminRegistration } from './interfaces';
import routes from '../../common/constants/routes';
import { checkEmailAvailable } from '../services/RegistrationService';
import { useAppDispatch, useAppSelector, useAppThunkDispatch } from '../../common/hooks';
import { getProfile, loginUser, logout, selectSecurity } from '../../common/store/securitySlice';
import { RestApi } from '../../common/services';

export const User: React.FC<{ state: IAdminRegistration }> = (props) => {
  const namespace = 'AdminRegistration.User';

  const { state } = props;

  const hasData = state?.building && state.legalEntity;
  const address = state?.building?.address;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const thunkDispatch = useAppThunkDispatch();
  const security = useAppSelector(selectSecurity);

  const [mode, setMode] = useState<'form' | 'login'>('form');
  const [loginError, setLoginError] = useState(false);

  // /registration/ je mimo PrivateRoute — při hard refreshi s uloženým tokenem by jinak
  // přihlášený uživatel zůstal ve stavu notAuthenticated (zrcadlí logiku PrivateRoute)
  useEffect(() => {
    if (security.token && security.state === 'notAuthenticated') thunkDispatch(getProfile());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToSummary = () => {
    const { user: _user, ...rest } = state;
    navigate(`${routes.registration}summary/`, { state: { ...rest, existingUser: true } });
  };

  const formik = useFormik<NonNullable<IAdminRegistration['user']> & { passwordCheck: string }>({
    initialValues: { firstName: '', lastName: '', email: '', phone: '', password: '', passwordCheck: '' },
    onSubmit: async (values) => {
      const isEmailAvailable = await checkEmailAvailable(values.email);

      if (isEmailAvailable === 'exists') {
        setLoginError(false);
        setMode('login');
      } else {
        const { passwordCheck: _passwordCheck, ...rest } = values;
        return navigate(`${routes.registration}summary/`, { state: { ...state, user: rest } });
      }
    },

    validationSchema: Yup.object({
      firstName: Yup.string().required(t('Form.Fields.FirstName.required', namespace)),
      lastName: Yup.string().required(t('Form.Fields.LastName.required', namespace)),
      email: YupEmail,
      phone: YupPhone,
      password: YupPassword,
      passwordCheck: Yup.string()
        .required()
        .oneOf([Yup.ref('password'), null], t('Profile.ChangePassword.Form.Fields.NewPasswordCheck.matches')),
    }),
  });

  const loginFormik = useFormik<{ password: string }>({
    initialValues: { password: '' },
    validate: () => setLoginError(false),
    validationSchema: Yup.object({
      password: Yup.string().required(t('ExistingAccount.Password.required', namespace)),
    }),
    onSubmit: (values) => {
      thunkDispatch(loginUser({ email: formik.values.email, password: values.password }))
        .unwrap()
        .then(() => goToSummary())
        .catch(() => {
          loginFormik.setSubmitting(false);
          setLoginError(true);
        });
    },
  });

  if (!hasData) return <IncompleteData />;

  if (security.state === 'authenticated') {
    return (
      <div className="OnboardingForm">
        <Row>
          <Col className="mb-2">
            <Text size="h1">{t('Authenticated.heading', namespace, { email: security.profile?.email })}</Text>
          </Col>
        </Row>
        <Row>
          <Col className="mb-4">
            <Text size="body-medium">{t('Authenticated.subheading', namespace, { address })}</Text>
          </Col>
        </Row>
        <Row>
          <Col size="12" className="mb-2">
            <Button title={t('Authenticated.Submit.title', namespace)} onClick={goToSummary} />
          </Col>
        </Row>
        <Row>
          <Col className="mb-2">
            <Text
              size="body-medium"
              weight="600"
              color="Primary500"
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                RestApi.removeToken();
                dispatch(logout());
                setMode('form');
              }}
            >
              {t('Authenticated.switchAccount', namespace)}
            </Text>
          </Col>
        </Row>
      </div>
    );
  }

  if (mode === 'login') {
    return (
      <form className="OnboardingForm" onSubmit={loginFormik.handleSubmit}>
        <Row>
          <Col className="mb-2">
            <Text size="h1">{t('ExistingAccount.heading', namespace, { email: formik.values.email })}</Text>
          </Col>
        </Row>
        <Row>
          <Col className="mb-2">
            <Text size="body-medium">{t('ExistingAccount.subheading', namespace)}</Text>
          </Col>
        </Row>
        <Row>
          <Col>{loginError && <FormMessage message={t('ExistingAccount.error', namespace)} />}</Col>
        </Row>
        <Row>
          <Col size="12" className="mb-2 mt-2">
            <TextField
              name="email"
              value={formik.values.email}
              label={t('Form.Fields.Email.label', namespace)}
              disabled={true}
            />
          </Col>
        </Row>
        <Row>
          <Col size="12" className="mb-2">
            <TipMessage>{t('ExistingAccount.ownerTip', namespace)}</TipMessage>
          </Col>
        </Row>
        <Row>
          <Col size="12" className="mb-4">
            <PasswordField
              name="password"
              autocomplete="current-password"
              placeholder={t('ExistingAccount.Password.placeholder', namespace)}
              label={t('ExistingAccount.Password.label', namespace)}
              value={loginFormik.values.password}
              onChange={loginFormik.handleChange}
              error={{ touched: loginFormik.touched.password, message: loginFormik.errors.password }}
            />
          </Col>
        </Row>
        <Row>
          <Col size="12" className="mb-2">
            <Button
              title={t('ExistingAccount.Submit.title', namespace)}
              type="submit"
              loading={loginFormik.isSubmitting}
            />
          </Col>
        </Row>
        <Row>
          <Col size="6" className="mb-2">
            <Text
              size="body-medium"
              weight="600"
              color="Primary500"
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => setMode('form')}
            >
              {t('ExistingAccount.useDifferentEmail', namespace)}
            </Text>
          </Col>
          <Col size="6" className="mb-2">
            {/* target=_blank — navigace ve stejné záložce by zahodila stav wizardu (předává se přes location state) */}
            <Link to={routes.passwordReset} target="_blank" className="text Primary500">
              {t('ExistingAccount.forgottenPassword', namespace)}
            </Link>
          </Col>
        </Row>
      </form>
    );
  }

  return (
    <form className="OnboardingForm" onSubmit={formik.handleSubmit}>
      <Row>
        <Col className="mb-2">
          <Text size="h1">{t('heading', namespace, { address })}</Text>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col size="6" className="mb-2">
          <TextField
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            placeholder={t('Form.Fields.FirstName.placeholder', namespace)}
            label={t('Form.Fields.FirstName.label', namespace)}
            error={{ touched: formik.touched.firstName, message: formik.errors.firstName }}
          />
        </Col>
        <Col size="6" className="mb-2">
          <TextField
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            placeholder={t('Form.Fields.LastName.placeholder', namespace)}
            label={t('Form.Fields.LastName.label', namespace)}
            error={{ touched: formik.touched.lastName, message: formik.errors.lastName }}
          />
        </Col>
      </Row>

      <Row>
        <Col size="6" className="mb-2">
          <TextField
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            placeholder={t('Form.Fields.Email.placeholder', namespace)}
            label={t('Form.Fields.Email.label', namespace)}
            error={{ touched: formik.touched.email, message: formik.errors.email }}
          />
        </Col>
        <Col size="6" className="mb-2">
          <PhoneField
            name="phone"
            value={formik.values.phone}
            onChange={(phone) => formik.setFieldValue('phone', phone)}
            placeholder={t('Form.Fields.Phone.placeholder', namespace)}
            label={t('Form.Fields.Phone.label', namespace)}
            error={{ touched: formik.touched.phone, message: formik.errors.phone }}
          />
        </Col>
      </Row>

      <Row>
        <Col size="12" className="mb-2">
          <TipMessage title={t('EmailTip.title', namespace)}>{t('EmailTip.text', namespace)}</TipMessage>
        </Col>
      </Row>

      <Row>
        <Col size="6" className="mb-2">
          <PasswordField
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            placeholder={t('Form.Fields.Password.placeholder', namespace)}
            label={t('Form.Fields.Password.label', namespace)}
            error={{ touched: formik.touched.password, message: formik.errors.password }}
          />
        </Col>
        <Col size="6" className="mb-2">
          <PasswordField
            name="passwordCheck"
            value={formik.values.passwordCheck}
            onChange={formik.handleChange}
            placeholder={t('Form.Fields.PasswordCheck.placeholder', namespace)}
            label={t('Form.Fields.PasswordCheck.label', namespace)}
            error={{ touched: formik.touched.passwordCheck, message: formik.errors.passwordCheck }}
          />
        </Col>
      </Row>

      <Row>
        <Col size="12" className="mb-2">
          <Button title={t('Form.Fields.Submit.title', namespace)} type="submit" loading={formik.isSubmitting} />
        </Col>
      </Row>
    </form>
  );
};
